import { useEffect, useRef, useState, useCallback } from 'react';
import { useReducedMotion } from './useReducedMotion';

type AnimationType = 'fade' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale' | 'fade-scale';

interface UseScrollAnimationOptions {
  threshold?: number;
  triggerOnce?: boolean;
  delay?: number;
  type?: AnimationType;
  staggerDelay?: number;
  staggerIndex?: number;
}

interface ScrollAnimationResult<T extends HTMLElement> {
  ref: React.RefObject<T>;
  isVisible: boolean;
  animationClass: string;
}

export function useScrollAnimation<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollAnimationOptions = {}
): ScrollAnimationResult<T> {
  const {
    threshold = 0.1,
    triggerOnce = true,
    delay = 0,
    type = 'fade',
    staggerDelay = 0,
    staggerIndex = 0,
  } = options;

  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const getAnimationClass = useCallback(() => {
    if (prefersReducedMotion) {
      return isVisible ? 'opacity-100' : 'opacity-0';
    }

    const baseHidden = 'opacity-0';
    const baseVisible = 'opacity-100';
    
    const animations: Record<AnimationType, { hidden: string; visible: string }> = {
      'fade': {
        hidden: `${baseHidden}`,
        visible: `${baseVisible}`,
      },
      'slide-up': {
        hidden: `${baseHidden} translate-y-8`,
        visible: `${baseVisible} translate-y-0`,
      },
      'slide-left': {
        hidden: `${baseHidden} translate-x-8`,
        visible: `${baseVisible} translate-x-0`,
      },
      'slide-right': {
        hidden: `${baseHidden} -translate-x-8`,
        visible: `${baseVisible} translate-x-0`,
      },
      'scale': {
        hidden: `${baseHidden} scale-95`,
        visible: `${baseVisible} scale-100`,
      },
      'fade-scale': {
        hidden: `${baseHidden} scale-90`,
        visible: `${baseVisible} scale-100`,
      },
    };

    const animation = animations[type];
    const totalDelay = delay + (staggerDelay * staggerIndex);
    const delayClass = totalDelay > 0 ? `delay-[${totalDelay}ms]` : '';
    
    return `transition-all duration-700 ease-out ${delayClass} ${
      isVisible ? animation.visible : animation.hidden
    }`;
  }, [isVisible, type, delay, staggerDelay, staggerIndex, prefersReducedMotion]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If reduced motion is preferred, show immediately
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const totalDelay = delay + (staggerDelay * staggerIndex);
            if (totalDelay > 0) {
              setTimeout(() => setIsVisible(true), totalDelay);
            } else {
              setIsVisible(true);
            }
            
            if (triggerOnce) {
              observer.unobserve(element);
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, triggerOnce, delay, staggerDelay, staggerIndex, prefersReducedMotion]);

  return {
    ref,
    isVisible,
    animationClass: getAnimationClass(),
  };
}

// Hook for staggered animations in lists
export function useStaggeredAnimation(
  itemCount: number,
  baseDelay: number = 100
) {
  const [visibleItems, setVisibleItems] = useState<boolean[]>(
    new Array(itemCount).fill(false)
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (prefersReducedMotion) {
      setVisibleItems(new Array(itemCount).fill(true));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Stagger the visibility of each item
            for (let i = 0; i < itemCount; i++) {
              setTimeout(() => {
                setVisibleItems((prev) => {
                  const newVisible = [...prev];
                  newVisible[i] = true;
                  return newVisible;
                });
              }, i * baseDelay);
            }
            observer.unobserve(container);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(container);

    return () => {
      observer.unobserve(container);
    };
  }, [itemCount, baseDelay, prefersReducedMotion]);

  return { containerRef, visibleItems };
}
